import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Form, { ClearAlertForm } from "./Form"
import AlertLayout from "../AlertLayout"
import AlertDetailRow from "../AlertLayout/AlertDetailRow"
import { getLevelIcon, groupLevelsByType } from "../utils"
import { AlertModalType } from "@/components/AlertModal"
import { IconColor } from "@/components/shared/Icon"
import { HttpCodes } from "@/constants/globals"
import { AppDispatch, RootState } from "@/store"
import { clearAlert } from "@/store/alertsSlice"
import { selectInmateById } from "@/store/inmatesSlice"
import { getErrorMessage, getFullInmateInfo } from "@/utils/index"
import "./index.scss"

export interface ClearAlertModalProps {
   open: boolean
   id: string
   levels: { name: string; type: string }[]
   alertType?: AlertModalType
   locationName: string
   locationType?: string
   inmateId?: string
   onClose: () => void
}
const ClearAlertModal: FC<ClearAlertModalProps> = ({
   open,
   id,
   levels,
   alertType,
   locationName,
   locationType,
   inmateId,
   onClose,
}) => {
   const dispatch = useDispatch<AppDispatch>()
   const { loadingClearAlert, errorClearAlert } = useSelector(
      ({ alertsReducer }: RootState) => alertsReducer
   )
   const inmate = useSelector((state: RootState) =>
      selectInmateById(state, inmateId)
   )
   const wearableMacAddress = inmate?.sensor?.macAddress ?? ""
   const inmateDisplayName =
      inmate?.customIdentifier || getFullInmateInfo(inmate)
   const [isSuccess, setIsSuccess] = useState(false)

   const groupedLevels = useMemo(
      () => groupLevelsByType(levels ?? []),
      [levels]
   )

   const handleClearAlert = useCallback(
      async (clearReport: ClearAlertForm) => {
         try {
            const result = await dispatch(
               clearAlert({
                  alertId: id,
                  clearReport,
               })
            ).unwrap()
            if (result.status === HttpCodes.OK) {
               setIsSuccess(true)
            }
         } catch (error) {
            console.error("Error clearing alert:", error)
         }
      },
      [dispatch, id]
   )

   useEffect(() => {
      return () => {
         setIsSuccess(false)
      }
   }, [])

   return (
      <AlertLayout
         open={open}
         iconColor={IconColor.WHITE}
         className="clear-alert-modal"
         headerIconName="safe"
         headerText="Clear alarm"
         description={
            isSuccess
               ? "Inmate checkup completed"
               : "Check up on the status of the inmate"
         }
         clearMode
         informativeIcons={
            <div className="alert-layout__informative-icons--clear-alert">
               {Object.entries(groupedLevels ?? {})
                  .reverse()
                  .map(([type, names]) => (
                     <AlertDetailRow
                        key={type}
                        iconColor={IconColor.WHITE}
                        iconName={getLevelIcon(type)}
                        value={names}
                        text={type}
                     />
                  ))}
               {alertType === AlertModalType.CELL ? (
                  <AlertDetailRow
                     iconName="cell"
                     iconColor={IconColor.WHITE}
                     value={locationName ?? ""}
                     text="Cell"
                  />
               ) : (
                  <>
                     <AlertDetailRow
                        iconName="location"
                        iconColor={IconColor.WHITE}
                        value={locationName ?? ""}
                        text={locationType ?? ""}
                     />
                     <AlertDetailRow
                        iconName="smartwatch"
                        iconColor={IconColor.WHITE}
                        value={wearableMacAddress}
                        text="ReassureBand"
                     />
                     <AlertDetailRow
                        iconName="inmate"
                        iconColor={IconColor.WHITE}
                        value={inmateDisplayName}
                        text="Inmate"
                     />
                  </>
               )}
            </div>
         }
         onClose={onClose}
      >
         <Form
            loading={loadingClearAlert}
            isSuccess={isSuccess}
            title="Please confirm checkup"
            description="Enter the form below to conclude the check-up and turn off alarms."
            reportNotePlaceholder="Situation report:
            please do not enter personal information about yourself or the inmate in this field."
            buttonText="Inmate checked. Clear Alarm."
            successTitle="Checkup confirmed"
            successDescription={
               <>
                  Thank you for confirming the inmate’s status,
                  <br /> the alarm has been turned off.
               </>
            }
            errorMessage={
               (errorClearAlert && getErrorMessage(errorClearAlert)) || null
            }
            onClick={handleClearAlert}
         />
      </AlertLayout>
   )
}

export default ClearAlertModal
